import { useTranslation } from 'react-i18next';
import { cn } from './cn';

/**
 * The orbit glyph: a planet with a moon circling it. Draws in `currentColor`; `accent` colours the
 * moon. Decorative (aria-hidden): wrap it in something that says what is loading.
 */
export function OrbitGlyph({ size = 16, accent = 'currentColor', className }: { size?: number; accent?: string; className?: string }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden focusable="false" className={cn('shrink-0', className)}>
      <circle cx="12" cy="12" r="9" fill="none" stroke="currentColor" strokeOpacity="0.18" strokeWidth="1.5" />
      <circle cx="12" cy="12" r="4.5" fill="currentColor" fillOpacity="0.55" />
      <g className="origin-center animate-orbit motion-reduce:animate-none" style={{ transformBox: 'view-box' }}>
        <circle cx="21" cy="12" r="2.4" fill={accent} />
      </g>
    </svg>
  );
}

/** Indeterminate progress: the orbit glyph plus an accessible label ("Loading" by default). */
export function Spinner({ size = 16, label, showLabel, className }: { size?: number; label?: string; showLabel?: boolean; className?: string }) {
  const { t } = useTranslation();
  const text = label ?? t('loading');
  return (
    <span role="status" aria-label={showLabel ? undefined : text} className={cn('inline-flex items-center gap-2 text-sm text-navy', className)}>
      <OrbitGlyph size={size} accent="var(--color-brand)" />
      {showLabel && <span className="text-ink-muted">{text}</span>}
    </span>
  );
}

/** A centred spinner filling its container: route and section loading states. */
export function PageSpinner({ label }: { label?: string }) {
  const { t } = useTranslation();
  return (
    <div className="flex min-h-48 items-center justify-center">
      <Spinner size={22} label={label ?? t('loadingEllipsis')} showLabel />
    </div>
  );
}
